/**
 * SearchAiz Tiles - Barrel Export
 *
 * All tile components for displaying search results.
 * Each tile is specialized for a specific data source type.
 */

// Base tile and utilities
export { BaseTile, ScoreBadge, SourceBadge, createTile } from './BaseTile';
export type { BaseTileRenderProps } from './BaseTile';

// Specialized tiles
export { DocumentTile } from './DocumentTile';
export { ProductTile } from './ProductTile';
export { CustomerTile } from './CustomerTile';
export { CallTile } from './CallTile';
export { EntityTile } from './EntityTile';
export { PageTile } from './PageTile';
export { SupportTile } from './SupportTile';
export { RealtimeTile } from './RealtimeTile';
export { OperationsTile } from './OperationsTile';
export { MetricTile } from './MetricTile';
export { WebTile } from './WebTile';

// Re-export types for convenience
export type {
  BaseTileProps,
  TileAction,
  TileDisplayMode,
  SearchResult,
  DocumentResult,
  ProductResult,
  CustomerResult,
  CallResult,
  EntityResult,
  PageResult,
  SupportResult,
  MetricResult,
} from '../types';

// Tile registry for dynamic rendering
import type { ComponentType } from 'react';
import type { BaseTileProps, ResultType, SearchResult } from '../types';
import { BaseTile } from './BaseTile';
import { DocumentTile } from './DocumentTile';
import { ProductTile } from './ProductTile';
import { CustomerTile } from './CustomerTile';
import { CallTile } from './CallTile';
import { EntityTile } from './EntityTile';
import { PageTile } from './PageTile';
import { SupportTile } from './SupportTile';
import { RealtimeTile } from './RealtimeTile';
import { OperationsTile } from './OperationsTile';
import { MetricTile } from './MetricTile';
import { WebTile } from './WebTile';

/**
 * Type for tile components that can render any SearchResult.
 */
type TileComponent = ComponentType<BaseTileProps<SearchResult>>;

/**
 * Map of result types to their tile components.
 */
export const TILE_REGISTRY: Record<ResultType, TileComponent> = {
  document: DocumentTile as unknown as TileComponent,
  repository: DocumentTile as unknown as TileComponent,
  product: ProductTile as unknown as TileComponent,
  customer: CustomerTile as unknown as TileComponent,
  contact: CustomerTile as unknown as TileComponent,
  order: SupportTile as unknown as TileComponent,
  ticket: SupportTile as unknown as TileComponent,
  call: CallTile as unknown as TileComponent,
  entity: EntityTile as unknown as TileComponent,
  page: PageTile as unknown as TileComponent,
  setting: PageTile as unknown as TileComponent,
  metric: MetricTile as unknown as TileComponent,
  realtime: RealtimeTile as unknown as TileComponent,
  support: SupportTile as unknown as TileComponent,
  operations: OperationsTile as unknown as TileComponent,
  web: WebTile as unknown as TileComponent,
};

/**
 * Get the tile component for a result type
 */
export function getTileComponent(
  type: ResultType
): ComponentType<BaseTileProps<SearchResult>> {
  return TILE_REGISTRY[type] ?? BaseTile;
}

/**
 * Render a result with the appropriate tile component
 */
export function renderResultTile(
  result: SearchResult,
  props: Omit<BaseTileProps, 'result'>
) {
  const TileComponent = getTileComponent(result.type);
  return <TileComponent result={result} {...props} />;
}
